// cspell:ignore Tweens
import { afterRenderEffect, ChangeDetectionStrategy, Component, ElementRef, input, viewChildren } from '@angular/core';
import { gsap } from 'gsap';
import { CountdownSegments } from '../countdown.model';

/**
 * A Countdown Section is used by a {@link CountdownComponent} to display one
 * unit of time (days / hours / minutes / seconds) as split-flap digits.
 *
 * The flip is driven by GSAP: whenever a digit changes, the top overlay
 * (already showing the next digit) folds down, then the bottom overlay
 * unfolds — the classic split-flap motion.
 *
 * ### CSS Custom Properties
 *
 * Set these from any ancestor to restyle the component — they inherit through
 * view encapsulation, so no `::ng-deep` or `:host-context` is needed.
 *
 * | Property | Description | Default |
 * | --- | --- | --- |
 * | `--ds-countdown-segment-size` | Width and font size of one digit | `30px` (`40px` from the ds breakpoint) |
 * | `--ds-countdown-top-background` | Background of the upper digit half | `#111` |
 * | `--ds-countdown-bottom-background` | Background of the lower digit half | `#333` |
 * | `--ds-countdown-color` | Bright digit ink | `#fff` |
 * | `--ds-countdown-color-dim` | Dimmed digit ink | `#eee` |
 */
@Component({
  selector: 'app-countdown-section',
  templateUrl: './countdown-section.component.html',
  styleUrls: ['./countdown-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownSectionComponent {
  /**
   * The configuration for this unit of time, carrying the prev/next digit
   * pair that drives the flip.
   */
  readonly config = input<CountdownSegments | null>();

  private readonly overlayTops = viewChildren<ElementRef<HTMLElement>>('overlayTop');
  private readonly overlayBottoms = viewChildren<ElementRef<HTMLElement>>('overlayBottom');

  /**
   * The digits as of the last animation pass, so a flip only runs for digits
   * that changed. Undefined until the first pass — every digit flips once on
   * first render (matching the legacy `* => *` Angular trigger, which also
   * matched the initial `void` transition).
   */
  private animatedDigits?: string[];

  constructor() {
    afterRenderEffect(() => {
      const config = this.config();
      const tops = this.overlayTops();
      const bottoms = this.overlayBottoms();

      if (!config) {
        this.animatedDigits = undefined;
        return;
      }

      const digits = config.next.asString;
      digits.forEach((digit, index) => {
        const top = tops[index]?.nativeElement;
        const bottom = bottoms[index]?.nativeElement;
        if (!top || !bottom || this.animatedDigits?.[index] === digit) {
          return;
        }
        this.flip(top, bottom);
      });
      this.animatedDigits = [...digits];
    });
  }

  /**
   * The split-flap motion: the top overlay folds down over the first half,
   * then the bottom overlay unfolds over the second half.
   *
   * Transforms are cleared at the end — the resting DOM shows the next digit
   * on every face, so the cleared state is visually identical to the final
   * animation frame.
   */
  private flip(top: HTMLElement, bottom: HTMLElement) {
    // a tick can land mid-flip; the new flip takes over cleanly
    gsap.killTweensOf([top, bottom]);
    gsap
      .timeline({ defaults: { duration: 0.25, ease: 'power1.out' } })
      .fromTo(top, { rotationX: 0, transformOrigin: 'center bottom' }, { rotationX: -90 })
      .fromTo(bottom, { rotationX: 90, transformOrigin: 'center top' }, { rotationX: 0 }, '>')
      .set([top, bottom], { clearProps: 'transform,transformOrigin' });
  }
}
